import React from 'react'
import PropTypes from 'prop-types'
import { Button, Col, Dropdown, Menu, message, Popover, Row, Spin, Table } from 'antd'
import { DownOutlined, DownloadOutlined } from '@ant-design/icons'
import FileSaver from 'file-saver'
import moment from 'moment'
import { get } from 'lodash'
import NumberFormat from 'react-number-format'
import classNames from 'classnames'

import { BaseComponent, PointBonus } from 'src/components'
import { lib, permissions, request, requestHelper } from 'src/Utils'
import typeCodes from 'src/Utils/static/typeCodes'
import staticProd from 'src/Utils/static/productionJobs'

const KEY_MSG_DOWNLOAD = 'downloading-qs'

class QSProductionDetail extends BaseComponent {
  constructor(props) {
    super(props)

    // ----- support for override -----
    this.allowExport = true
    this.KEY_TITLE_PRODUCTION = 'Productions'
    this.KEY_INDEX_AMOUNT = 'qs_value'
    // --------------------------------

    this.myProfile = lib.ls.getCurrentUser()
    const canViewPointReward = permissions.canViewPointReward(this.myProfile)

    // Jika ingin di beri scroll Y atau fixed columns, beri width di setiap kolom
    this.scrollOpts = { x: 912, y: 'calc(100vh - 135px)' }
    this.scrollOpts = { x: 912 }
    this.stickyOpt = { offsetHeader: 90 }
    this.columns = [
      {
        title: 'QS List',
        dataIndex: 'fullName',
        fixed: 'left',
        width: '140px',
        onCell: () => ({ style: { borderRightColor: '#c3c3c3' } }),
        onHeaderCell: () => ({ style: { borderRightColor: '#c3c3c3' } }),
        // render: user => lib.getFullName(user),
        render: fullName => fullName,
      },
      {
        title: this.KEY_TITLE_PRODUCTION,
        onHeaderCell: () => ({ style: { borderRightColor: '#c3c3c3' } }),
        children: [
          {
            title: 'Amount',
            // dataIndex: ['prod_summary', 'qs_value'],
            dataIndex: this.KEY_INDEX_AMOUNT,
            align: 'right',
            width: '90px',
            sortDirections: ['descend', 'ascend'],
            sorter: (a, b) => a.qs_value - b.qs_value,
            render: val => <NumberFormat thousandSeparator displayType='text' value={Number(val).toFixed(2)} />,
          },
          {
            title: 'Jobs',
            // dataIndex: ['prod_summary', 'total_jobs'],
            dataIndex: 'total_jobs',
            align: 'right',
            width: '70px',
            sortDirections: ['descend', 'ascend'],
            sorter: (a, b) => a.total_jobs - b.total_jobs,
            getSummary: (currentData) => currentData.reduce((acc, val) => acc + val.total_jobs, 0),
          },
          {
            title: 'Area',
            // dataIndex: ['prod_summary', 'total_area'],
            dataIndex: 'total_area',
            align: 'right',
            width: '80px',
            sortDirections: ['descend', 'ascend'],
            sorter: (a, b) => a.total_area - b.total_area,
            onCell: () => !canViewPointReward && ({ style: { borderRightColor: '#c3c3c3' } }),
            onHeaderCell: () => !canViewPointReward && ({ style: { borderRightColor: '#c3c3c3' } }),
            render: val => <NumberFormat thousandSeparator displayType='text' value={Number(val)} />,
            getSummary: (currentData) => currentData.reduce((acc, val) => acc + val.total_area, 0),
          },
          ...(!canViewPointReward ? [] : [
            {
              title: 'Point',
              dataIndex: 'actualPoint',
              align: 'right',
              width: '80px',
              sortDirections: ['descend', 'ascend'],
              sorter: (a, b) => a.actualPoint - b.actualPoint,
              onCell: () => ({ style: { borderRightColor: '#c3c3c3' } }),
              onHeaderCell: () => ({ style: { borderRightColor: '#c3c3c3' } }),
              render: (val, record) => !this.myProfile.is_owner ? Number(val).toFixed(2) : (
                <PointBonus
                  point={val}
                  prodMonth={this.props.dateString}
                  user={record.user}
                  typeExchangeFactor={record.qsExchangeFactor}
                  effectiveExchange={this.props.effectiveExchange}
                  {...this.getPointBonusProps()}
                />
              )
            },
          ])
        ]
      },
      {
        title: 'Number of jobs by Grade',
        onHeaderCell: () => ({ style: { borderRightColor: '#c3c3c3' } }),
        children: []
      },
      {
        title: 'Area m² by Grade',
        children: []
      }
    ]

    this.state = {
      data: this.getData(props.productionJobs),
      downloading: false,
      isLoading: false,
    }
  }

  getPointBonusProps = () => null
  getProductionJobsColumns = (productionJobs = this.props.productionJobs) => productionJobs
  getProductionJobsData = (productionJobs = this.props.productionJobs) => productionJobs

  getData = (productionJobs) => {
    // Set grade columns
    this.insertGradeColumns(this.getProductionJobsColumns(productionJobs))

    // GROUPING JOBS BY USER ID
    const groupedJobs = {}
    productionJobs = this.getProductionJobsData(productionJobs)
    for (let i = 0; i < productionJobs.length; i += 1) {
      if (!productionJobs[i].user) continue

      const { id } = productionJobs[i].user

      if (!groupedJobs[id])
        groupedJobs[id] = { jobs: [], objGrade: {} }

      groupedJobs[id].jobs.push(productionJobs[i])
      groupedJobs[id].user = productionJobs[i].user

      if (!groupedJobs[id].objGrade[productionJobs[i].grade])
        groupedJobs[id].objGrade[productionJobs[i].grade] = []

      groupedJobs[id].objGrade[productionJobs[i].grade].push(productionJobs[i])
    }

    // SUM VALUE OF GROUPED JOBS
    const data = []
    for (const userId in groupedJobs) {
      const user = this.props.userTypes.find(user => String(user.id) === userId)
      // const userPointTypeQS = user.point_types.find(pointType => typeCodes.QS_CODES.includes(pointType.code))
      // semua job harus di tampilkan, supaya amount sama dengan total amount di Production Detail
      const userPointTypeQS = !!user // true
      // console.log('here', userId, user, this.props.userTypes)

      // hanya menampilkan QS jobs
      if (userPointTypeQS) {
        // initial record
        const record = {
          user: {
            ...user,
            // point_types: [userPointTypeQS],
            point_types: user.point_types.filter(pointType => typeCodes.QS_CODES.includes(pointType.code)),
          },
          fullName: lib.getFullName(user),
          objGrade: groupedJobs[userId].objGrade,
          qs_value: 0,
          total_area: 0,
          total_jobs: 0,
          qs_point: 0,
        }

        record.qsExchangeFactor = get(record.user.point_types.find(type => typeCodes.QS_CODES.includes(type.code)), 'exchange_factor', staticProd.DEFAULT_EXCHANGE_FACTOR)

        for (let i = 0; i < groupedJobs[userId].jobs.length; i += 1) {
          const { qs_value, area, qs_point } = groupedJobs[userId].jobs[i]

          record.qs_value += Number(qs_value)
          record.total_area += Number(area)
          record.total_jobs += 1

          // if (record.qsExchangeFactor) // di comment karena jika user bukan QS, maka point 0, sehingga total point di QS Production dan Detail tidak sama.
            record.qs_point += Number(qs_point)
        }

        record.actualPoint = Math.round(Number(record.qs_point) * 100) / 100
        
        data.push(record)
      }
    }

    data.sort((a, b) => a.fullName > b.fullName ? 1 : -1)

    return data
  }

  // access from props
  setData = (productionJobs) => {
    this.columns[2].children = []
    this.columns[3].children = []

    this.setThisState({ data: this.getData(productionJobs) })
  }

  insertGradeColumns = (productionJobs) => {
    const grades = []
    for (let i = 0; i < productionJobs.length; i += 1) {
      if (grades.findIndex(grade => grade === productionJobs[i].grade) === -1) {
        grades.push(productionJobs[i].grade)
      }
    }

    for (let i = 0; i < grades.length; i += 1) {
      // Number of jobs by Grade
      this.columns[2].children.push({
        title: grades[i],
        dataIndex: ['objGrade', grades[i]],
        align: 'center',
        width: '60px',
        render: grades => (grades || []).length || '-',
        // render: grades => (grades || []).reduce((acc, record) => acc + Number(record.prod_day), 0) || '-',
      })

      // Area m² by Grade
      this.columns[3].children.push({
        title: grades[i],
        dataIndex: ['objGrade', grades[i]],
        align: 'center',
        width: '60px',
        render: grades => {
          const area = (grades || []).reduce((acc, record) => acc + Number(record.area), 0)
          return !area ? '-' : <NumberFormat thousandSeparator displayType='text' value={Number(area)} />
        }
      })
    }

    this.columns[2].children.sort((a, b) => a.title > b.title ? 1 : -1)
    this.columns[3].children.sort((a, b) => a.title > b.title ? 1 : -1)

    const childLength = this.columns[2].children.length
    if (childLength) {
      this.columns[2].children[childLength - 1].onCell = () => ({ style: { borderRightColor: '#c3c3c3' } })
      this.columns[2].children[childLength - 1].onHeaderCell = () => ({ style: { borderRightColor: '#c3c3c3' } })
    }
  }

  // check and insert grade columns
  // insertGradeColumn = (gradeLabel) => {
  //   if (this.columns[2].children.findIndex(col => col.title === gradeLabel) !== -1)
  //     return

  //   // Number of jobs by Grade
  //   this.columns[2].children.push({
  //     title: gradeLabel,
  //     dataIndex: ['objGrade', gradeLabel],
  //     align: 'center',
  //     width: '60px',
  //     render: grades => (grades || []).length || '-',
  //     // render: grades => (grades || []).reduce((acc, record) => acc + Number(record.prod_day), 0) || '-',
  //   })

  //   // Area m² by Grade
  //   this.columns[3].children.push({
  //     title: gradeLabel,
  //     dataIndex: ['objGrade', gradeLabel],
  //     align: 'center',
  //     width: '60px',
  //     render: grades => (grades || []).reduce((acc, record) => acc + Number(record.area), 0) || '-',
  //   })
  // }

  // read = (dateString = this.props.dateString) => {
  //   if (this.state.isLoading)
  //     return

  //   this.setThisState({ isLoading: true }, () => {
  //     request({
  //       urlKey: 'read-productionJobsQS',
  //       data: {
  //         finished_date_after: moment(dateString).startOf('month').format(),
  //         finished_date_before: moment(dateString).endOf('month').format(),
  //       },
  //       onSuccess: this.readSuccess,
  //       onFailed: this.readFailed,
  //     })
  //   })
  // }

  // readSuccess = (response) => {
  //   this.columns[2].children = []
  //   this.columns[3].children = []

  //   const jobsByGrade = get(response.data[0], 'jobs_by_grade', [])
  //   for (const i in jobsByGrade) {
  //     this.columns[2].children.push({
  //       title: jobsByGrade[i].name,
  //       dataIndex: ['jobs_by_grade', i, 'total'],
  //       align: 'center',
  //       width: '60px',
  //       render: val => val || '-',
  //     })
  //   }

  //   const childLength = this.columns[2].children.length
  //   if (childLength) {
  //     this.columns[2].children[childLength - 1].onCell = () => ({ style: { borderRightColor: '#c3c3c3' } })
  //     this.columns[2].children[childLength - 1].onHeaderCell = () => ({ style: { borderRightColor: '#c3c3c3' } })
  //   }

  //   const areaByGrade = get(response.data[0], 'area_by_grade', [])
  //   for (const i in areaByGrade) {
  //     this.columns[3].children.push({
  //       title: areaByGrade[i].name,
  //       dataIndex: ['area_by_grade', i, 'total'],
  //       align: 'center',
  //       width: '60px',
  //       render: val => val || '-',
  //     })
  //   }

  //   this.setThisState({ isLoading: false, data: response.data })
  // }

  readFailed = (error) => {
    this.setThisState({ isLoading: false })
  }

  getExportMenu = () => {
    return (
      <Menu onClick={this.onExportClick}>
        <Menu.Item key='pdf' icon={<DownloadOutlined />}>
          Export to PDF
        </Menu.Item>
        <Menu.Item key='excel' icon={<DownloadOutlined />}>
          Export to EXCEL
        </Menu.Item>
      </Menu>
    )
  }

  onExportClick = ({ key }) => {
    if (this.state.downloading)
      return message.info('Please wait until previous export to finish')

    this.setThisState({ downloading: true }, () => {
      message.loading({ key: KEY_MSG_DOWNLOAD, content: `Downloading QS Production Detail (${key}) ...`, duration: 0 })
      requestHelper({
        urlKey: 'export-productionJobsQS',
        config: { responseType: 'blob' },
        data: {
          finished_date_after: moment(this.props.dateString).startOf('month').format(),
          finished_date_before: moment(this.props.dateString).endOf('month').format(),
          export_to: key,
          status: 'finished',
        },
        onSuccess: this.exportSuccess,
        onFailed: this.exportFailed,
        extra: {
          strParam: lib.getStrParam({
            finished_date_after: moment(this.props.dateString).startOf('month').format(),
            finished_date_before: moment(this.props.dateString).endOf('month').format(),
            export_to: key,
          })
        }
      })
    })
  }

  exportSuccess = (response, extra) => {
    const filename = lib.getFilenameDisposition(response.headers)
    // FileDownload(response.data, filename)
    // return

    // const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    // FileSaver.saveAs('https://timewise.things.id/api/dashboard/reward/production-jobs-qs/export/?finished_date_after=2021-05-01T00:00:00%2B07:00&finished_date_before=2021-05-31T23:59:59%2B07:00&export_to=excel')
    // FileSaver.saveAs(`${appConfig.url.api}/${urls['export-productionJobsQS']}${extra.strParam}`, filename)
    FileSaver.saveAs(response.data, filename)

    // axios({
    //   method: 'GET',
    //   url: `${appConfig.url.api}/${urls['export-productionJobsQS']}${extra.strParam}`,
    //   responseType: 'blob',
    // }).then(res => {
    //   console.log('test axios', res)
    // })

    // // response.data
    //   // .blob()
    //   // .then(blob => {
    //     console.log('blob', blob)
    //     const url = window.URL.createObjectURL(
    //       new Blob([blob], { type: response.headers['content-type'] })
    //     )
    //     console.log('object url created', url)
    //     const link = document.createElement('a')
    //     console.log('link created', link)
    //     link.href = url
    //     console.log('href')
    //     link.setAttribute('download', filename)
    //     console.log('attribute download')
    //     document.body.appendChild(link)
    //     console.log('append child link')
    //     link.click()
    //     console.log('link clicked')
    //     // this.setThisState({ isLoading: false, exportProcess: false })
    //     // message.destroy()
    //     message.success({ key: KEY_MSG_DOWNLOAD, content: 'File downloaded' })
    //   // })
    //   // .catch(e => {
    //   //   console.log('catch export', e)
    //   //   console.warn(e)
    //   //   message.error({ key: KEY_MSG_DOWNLOAD, content: 'Export failed!' })
    //   //   // message.destroy()
    //   //   // this.setThisState({ isLoading: false, exportProcess: false }, () =>
    //   //   //   message.error('Error when creating file')
    //   //   // )
    //   // })

    message.success({ key: KEY_MSG_DOWNLOAD, content: 'File downloaded' })
    this.setThisState({ downloading: false })
  }

  exportFailed = () => {
    message.error({ key: KEY_MSG_DOWNLOAD, content: 'Export failed!' })
    this.setThisState({ downloading: false })
  }

  summaryTable = (currentData) => {
    return (
      <Table.Summary.Row className='summary-production'>
        <Table.Summary.Cell index={0} className='cell-prod-border-right'>
          Total
        </Table.Summary.Cell>
        {/* <Table.Summary.Cell index={1} align='right'>
          <NumberFormat
            thousandSeparator
            displayType='text'
            value={currentData.reduce((acc, val) => acc + val.qs_value, 0).toFixed(2)}
          />
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} align='right'>
          <NumberFormat
            thousandSeparator
            displayType='text'
            value={currentData.reduce((acc, val) => acc + val.total_jobs, 0)}
          />
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align='right'>
          <NumberFormat
            thousandSeparator
            displayType='text'
            value={currentData.reduce((acc, val) => acc + val.total_area, 0)}
          />
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align='right' className='cell-prod-border-right'>
          <NumberFormat
            thousandSeparator
            displayType='text'
            value={currentData.reduce((acc, val) => acc + val.qs_point, 0).toFixed(2)}
          />
        </Table.Summary.Cell> */}
        {this.columns[1].children.map((productionCol, i) => (
          <Table.Summary.Cell index={i + 1} align='right' className={classNames({ 'cell-prod-border-right': i === this.columns[1].children.length - 1 })}>
            <NumberFormat
              thousandSeparator
              displayType='text'
              value={productionCol.getSummary
                ? productionCol.getSummary(currentData)
                : currentData.reduce((acc, val) => acc + get(val, productionCol.dataIndex), 0).toFixed(2)}
            />
          </Table.Summary.Cell>
        ))}
        {this.columns[2].children.map((gradeCol, i) => (
          <Table.Summary.Cell key={gradeCol.title} index={i + 4} align='center' className={classNames({ 'cell-prod-border-right': i === this.columns[2].children.length - 1 })}>
            <NumberFormat
              thousandSeparator
              displayType='text'
              value={currentData.reduce((acc, val) => acc + get(val, gradeCol.dataIndex, []).length, 0)}
            />
          </Table.Summary.Cell>
        ))}
        {this.columns[3].children.map((gradeCol, i) => (
          <Table.Summary.Cell key={gradeCol.title} index={i + 4 + this.columns[2].children.length} align='center'>
            <NumberFormat
              thousandSeparator
              displayType='text'
              value={currentData.reduce((acc, val) => acc + get(val, gradeCol.dataIndex, []).reduce((acc2, job) => acc2 + Number(job.area), 0), 0)}
            />
          </Table.Summary.Cell>
        ))}
      </Table.Summary.Row>
    )
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.productionJobs !== nextProps.productionJobs) {
      this.setData(nextProps.productionJobs)
    }
  }

  render() {
    window.konsole.log('Rendered', this)
    return (
      <div>
        <Row justify='space-between' className='sticky-qs-production'>
          <Col>
            <h2 style={{ marginBottom: 0 }}>
              QS Production
            </h2>
          </Col>
          <Col>
            <Row gutter={[6, 6]}>
              {/* <Col>
                <Button block icon={<ReloadOutlined />} loading={this.state.isLoading} onClick={() => this.read()}>
                  Refresh
                </Button>
              </Col> */}
              {this.allowExport && (
                <Col>
                  <Dropdown
                    disabled={this.props.loading}
                    placement='bottomRight'
                    trigger={['click']}
                    overlay={this.getExportMenu()}
                  >
                    <Button loading={this.state.downloading} icon={<DownOutlined />} style={{ minWidth: '100px' }}>
                      Export ({moment(this.props.dateString).format('MMMM YYYY')})
                    </Button>
                  </Dropdown>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
        <Spin spinning={this.props.loading}>
          <div className='app-card'>
            <Table
              bordered
              sticky={this.stickyOpt}
              size='small'
              rowKey={record => record.fullName}
              // className='ant-table-qsProduction'
              columns={this.columns}
              dataSource={this.state.data}
              pagination={false}
              scroll={this.scrollOpts}
              onRow={(_, index) => index % 2 === 1 && ({ className: 'even' })}
              summary={this.summaryTable}
            />
          </div>
        </Spin>
      </div>
    )
  }
}

QSProductionDetail.propTypes = {
  loading: PropTypes.bool,
  dateString: PropTypes.string,
  effectiveExchange: PropTypes.object,
  productionJobs: PropTypes.array,
  userTypes: PropTypes.array,
}

QSProductionDetail.defaultProps = {
  productionJobs: [],
  userTypes: [],
}

export default QSProductionDetail